import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Wand2, Loader2, Building2, Settings as SettingsIcon, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import onboardingService from '../../services/onboardingService';

interface BrandProfile {
  business_name?: string | null;
  brand_name?: string | null;
  brand_voice?: string | null;
  tone_of_voice?: string | null;
}

interface Props {
  engine: 'seedance' | 'heygen';
  /** Current text in the parent's textarea — passed so the AI can refine it */
  currentText: string;
  /** Called with the new generated text */
  onApply: (text: string) => void;
}

/**
 * Small "co-pilot" block to write the script/prompt for the parent textarea.
 * UX flow:
 *   1. Shows brand context banner so the user knows the AI is brand-aware
 *   2. User types a TOPIC ("Black Friday promo", "case study reveal", etc)
 *   3. Click → AI writes a full prompt (Seedance) or script (HeyGen) and
 *      drops it into the textarea above for the user to review/edit
 *
 * Designed to live ABOVE the main textarea — the user reads from top to bottom.
 */
const AIPromptHelper: React.FC<Props> = ({ engine, currentText, onApply }) => {
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState<BrandProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    onboardingService.getProfile()
      .then((p: any) => { if (p) setProfile(p); })
      .catch(() => {})
      .finally(() => setProfileLoaded(true));
  }, []);

  const brandName = profile?.business_name || profile?.brand_name;
  const brandTone = profile?.brand_voice || profile?.tone_of_voice;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await videoStudioService.aiPrompt({
        engine,
        topic: topic.trim() || undefined,
        currentText: currentText || undefined,
        language: i18n.language || 'pt'
      });
      if (r.success && r.data?.text) {
        onApply(r.data.text);
        toast.success(t('videoStudio.aiPrompt.applied', 'IA aplicou um novo texto') as string);
      } else {
        throw new Error('Empty response');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || (t('videoStudio.aiPrompt.failed', 'Falha ao gerar texto com IA') as string));
    } finally {
      setGenerating(false);
    }
  };

  const placeholderText = engine === 'heygen'
    ? (t('videoStudio.aiPrompt.topicPlaceholderHeygen', 'Ex: anúncio de Black Friday com 30% off pra novos clientes') as string)
    : (t('videoStudio.aiPrompt.topicPlaceholderSeedance', 'Ex: produto girando em estúdio com luz quente, ângulo baixo') as string);

  return (
    <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50/70 to-fuchsia-50/40 dark:from-purple-900/20 dark:to-fuchsia-900/10 p-3 space-y-2">
      {/* Brand context line — shown only if there IS a brand profile */}
      {profileLoaded && brandName && (
        <div className="flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
            <Building2 className="w-3 h-3" />
          </div>
          <span className="flex-1 min-w-0 truncate">
            {t('videoStudio.aiPrompt.basedOn', 'Gerando com base em')}{' '}
            <strong className="font-semibold">{brandName}</strong>
            {brandTone && <span className="text-gray-500 dark:text-gray-400"> · {brandTone}</span>}
            {' · '}
            <span className="text-purple-700 dark:text-purple-300 font-semibold">
              {engine === 'heygen'
                ? t('videoStudio.aiPrompt.formatHeygen', 'roteiro do apresentador')
                : t('videoStudio.aiPrompt.formatSeedance', 'prompt cinematográfico')}
            </span>
          </span>
          <RouterLink
            to="/settings/business-profile"
            className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition"
            title={t('common.edit', 'Editar')}
          >
            <SettingsIcon className="w-3 h-3" />
          </RouterLink>
        </div>
      )}
      {profileLoaded && !brandName && (
        <div className="flex items-center gap-2 text-[11px] text-amber-700 dark:text-amber-300">
          <Building2 className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1">
            {t('videoStudio.aiPrompt.noProfile', 'Configure seu perfil de negócio para a IA escrever no seu tom de voz.')}
          </span>
          <RouterLink
            to="/settings/business-profile"
            className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition"
          >
            <SettingsIcon className="w-3 h-3" />
            {t('common.configure', 'Configurar')}
          </RouterLink>
        </div>
      )}

      {/* Topic input + generate button */}
      <div className="flex gap-2 items-stretch">
        <input
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder={placeholderText}
          disabled={generating}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleGenerate(); } }}
          className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-purple-200 dark:border-purple-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/60 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 text-white text-sm font-semibold hover:brightness-110 disabled:opacity-50 transition shadow-sm whitespace-nowrap"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {t('videoStudio.aiPrompt.generating', 'Gerando…')}</>
          ) : (
            <>
              <Wand2 className="w-4 h-4" />
              {currentText.trim()
                ? t('videoStudio.aiPrompt.refineCta', 'Refinar')
                : t('videoStudio.aiPrompt.generateCta', 'Gerar com IA')}
              <Sparkles className="w-3 h-3 opacity-70" />
            </>
          )}
        </button>
      </div>
      <p className="text-[10px] text-gray-500 dark:text-gray-400">
        {currentText.trim()
          ? t('videoStudio.aiPrompt.refineHint', 'Diga o que mudar — a IA reescreve o texto abaixo.')
          : t('videoStudio.aiPrompt.generateHint', 'Diga sobre o que é o vídeo. A IA escreve o texto pronto pra você editar.')}
      </p>
    </div>
  );
};

export default AIPromptHelper;
